import type { LogsEvent } from '@datadog/browser-logs'

// Third-party hosts whose failed requests are almost always blocked by ad blockers or privacy
// extensions. They are not our bugs and made up about 10 % of the prod error stream.
const THIRD_PARTY_HOSTS = [
  'amplitude.com',
  'analytics.google.com',
  'abtasty.com',
  'usercentrics.eu',
]

// Messages that never point at a bug in our code:
// - ResizeObserver loop: benign browser quirk, about 70 % of the prod error stream
// - Script error.: opaque error from a cross-origin script, carries no information
// - Object Not Found Matching Id: thrown by mail link scanners that open the portal
const NOISE_MESSAGES = [
  'ResizeObserver loop',
  '"Script error."',
  'Object Not Found Matching Id:',
]

// The Amplitude SDK logs its own transport failures via console.error. Other Amplitude errors,
// e.g. "Request missing required field", can be our tracking bug and are kept.
const AMPLITUDE_LOGGER_PREFIX = 'Amplitude Logger [Error]:'
const AMPLITUDE_TRANSPORT_ERRORS = [
  'Load failed',
  'Failed to fetch',
  'NetworkError',
  'exceeded retry count',
  'No remote config',
]

const isThirdPartyHost = (url: string): boolean => {
  let hostname: string
  try {
    hostname = new URL(url).hostname
  } catch {
    return false
  }

  return THIRD_PARTY_HOSTS.some(
    (host) => hostname === host || hostname.endsWith(`.${host}`),
  )
}

export const isDatadogLogNoise = (log: LogsEvent): boolean => {
  const message = log.message ?? ''

  if (
    log.origin === 'network' &&
    log.http?.url &&
    isThirdPartyHost(log.http.url)
  ) {
    return true
  }

  if (NOISE_MESSAGES.some((noise) => message.includes(noise))) {
    return true
  }

  return (
    message.startsWith(AMPLITUDE_LOGGER_PREFIX) &&
    AMPLITUDE_TRANSPORT_ERRORS.some((error) => message.includes(error))
  )
}

export default isDatadogLogNoise
